import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Drop } from '@/types';
import { FiLock, FiPlay, FiCalendar, FiPackage, FiClock } from 'react-icons/fi';
import { formatAddress, formatDate } from '@/utils/formatters';
import { useAuthStore } from '@/store/authStore';
import { web3Service } from '@/services/web3';
import { motion } from 'framer-motion';

interface DropCardProps {
  drop: Drop;
  compact?: boolean;
}

const DropCard = ({ drop, compact = false }: DropCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated } = useAuthStore();

  // Get image URL from thumbnailUrl, coverImageUrl, or IPFS
  const imageUrl = drop.thumbnailUrl || drop.coverImageUrl || (drop.ipfsCid ? `https://ipfs.io/ipfs/${drop.ipfsCid}` : '');

  // Check if user has access to token-gated content
  useEffect(() => {
    const checkAccess = async () => {
      if (!drop.isTokenGated || !isAuthenticated || !user) {
        setHasAccess(!drop.isTokenGated);
        setIsLoading(false);
        return;
      }

      try {
        const access = await web3Service.hasAccess(user.walletAddress, drop.id);
        setHasAccess(access);
      } catch (error) {
        console.error('Error checking access:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [drop, user, isAuthenticated]);

  // Check if drop is scheduled for the future
  const isScheduled = drop.startTime && new Date(drop.startTime) > new Date();

  return (
    <Link
      to={`/drops/${drop.id}`}
      className="block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div 
        className={`relative overflow-hidden ${compact ? 'rounded-xl' : 'card card-hover shadow-3d'}`}
        whileHover={{ y: -8 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Drop Image */}
        <div className={`relative ${compact ? 'aspect-video' : 'aspect-video rounded-t-xl overflow-hidden'}`}>
          <div className="absolute inset-0 bg-dark-700 animate-pulse"></div>
          <img
            src={imageUrl}
            alt={drop.title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 via-transparent to-transparent"></div>
          
          {/* Token-gated badge */}
          {drop.isTokenGated && (
            <div className="absolute top-2 right-2 badge-outline flex items-center space-x-1 z-10">
              <FiLock size={12} />
              <span>{isLoading ? 'Checking...' : hasAccess ? 'Unlocked' : 'Token-Gated'}</span>
            </div>
          )}
          
          {/* Scheduled badge */}
          {isScheduled && (
            <div className="absolute top-2 left-2 badge-secondary flex items-center space-x-1 z-10">
              <FiCalendar size={12} />
              <span>{formatDate(drop.startTime)}</span>
            </div>
          )}
          
          {/* Play button overlay */}
          <motion.div 
            className="absolute inset-0 flex items-center justify-center bg-dark-900/50 opacity-0 transition-opacity z-10"
            animate={{ opacity: isHovered ? 1 : 0 }}
          >
            <motion.div 
              className="w-14 h-14 rounded-full bg-primary-600/80 backdrop-blur-sm flex items-center justify-center shadow-3d"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiPlay className="text-white ml-1" size={24} />
            </motion.div>
          </motion.div>
          
          {/* Drop type indicator */}
          <div className="absolute bottom-2 left-2 badge-primary flex items-center space-x-1 z-10">
            <FiPackage size={12} />
            <span>{drop.contentType || 'Media'}</span>
          </div>
          
          {/* Duration if available */}
          {drop.duration && (
            <div className="absolute bottom-2 right-2 badge-outline flex items-center space-x-1 z-10">
              <FiClock size={12} />
              <span>{formatDuration(drop.duration)}</span>
            </div>
          )}
        </div>
        
        {!compact && (
          <div className="p-4">
            {/* Drop Info */}
            <div className="flex items-start space-x-3">
              {/* Creator avatar */}
              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-dark-700">
                {drop.creator?.avatarUrl ? (
                  <img 
                    src={drop.creator.avatarUrl} 
                    alt={drop.creator.username} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(drop.creator?.username || 'U')}&background=7c3aed&color=fff`;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary-600 text-white text-sm font-bold">
                    {drop.creator?.username?.charAt(0).toUpperCase() || 'C'}
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium text-base mb-1 line-clamp-1">{drop.title}</h3>
                <div className="flex items-center text-gray-400 text-sm">
                  <span className="truncate">{drop.creator?.username || formatAddress(drop.creatorId)}</span>
                  <span className="mx-1">•</span>
                  <span>{formatTimeAgo(drop.createdAt)}</span>
                </div>
              </div>
            </div>
            
            {/* Description preview */}
            {drop.description && (
              <p className="mt-3 text-sm text-gray-400 line-clamp-2">
                {drop.description}
              </p>
            )}
            
            {/* Access indicator */}
            {drop.isTokenGated && !isLoading && (
              <div className={`mt-3 text-xs ${hasAccess ? 'text-primary-400' : 'text-accent-400'} flex items-center`}>
                <FiLock className="mr-1" size={12} />
                {hasAccess ? 'You have access to this content' : 'Requires FTTF tokens to unlock'}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </Link>
  );
};

// Helper function to format duration
const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Helper function to format time ago
const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  
  const years = Math.floor(days / 365);
  return `${years}y ago`;
};

export default DropCard;